import React, { useEffect, useState, useCallback } from 'react';
import { formatCurrency } from '../../tools/tool';
import { adminServices } from "../../services/AdminServices";
import IcarusServices from "../../services/IcarusServices";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
const TotalIncomeCard = ({
  dateRange
}) => {
  const navigate = useNavigate();
  const INITIAL_FEES = {
    kioskFees: 0,
    posFees: 0,
    walkinFees: 0,
    onlineFees: 0,
    regularOrder: 0,
    specialOrder: 0,
    grossProfit: 0
  };
  const [fees, setFees] = useState(INITIAL_FEES);
  const [isIncomeReady, setIsIncomeReady] = useState(false);
  const toNumber = value => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };
  const getTransactionFees = useCallback(async () => {
    try {
      setIsIncomeReady(false);
      const nextFees = {
        ...INITIAL_FEES
      };
      let kioskSP = 'service_fees';
      let params = {};
      if (dateRange.dateFrom && dateRange.dateTo) {
        params = {
          dateFrom: dateRange.dateFrom,
          dateTo: dateRange.dateTo
        };
        kioskSP = 'service_fees_range';
      } else {
        params = {};
      }
      try {
        const kioskResponse = await adminServices.storeProcedure2(kioskSP, params);
        if (kioskResponse.status === 200) {
          if (dateRange.dateFrom && dateRange.dateTo && Array.isArray(kioskResponse.data)) {
            const kioskTotal = kioskResponse.data.reduce((total, item) => total + Math.abs(item.service_fee || 0), 0);
            nextFees.kioskFees = toNumber(kioskTotal);
          } else {
            nextFees.kioskFees = toNumber(kioskResponse.data[0]?.service_fees || 0);
          }
        }
      } catch (error) {
        // keep default 0
      }
      let posSP = 'pos_fees';
      params = {};
      if (dateRange.dateFrom && dateRange.dateTo) {
        params = {
          dateFrom: dateRange.dateFrom,
          dateTo: dateRange.dateTo
        };
        posSP = 'pos_fees_range';
      } else {
        params = {};
      }
      try {
        const posResponse = await adminServices.storeProcedure2(posSP, params);
        if (posResponse.status === 200) {
          if (dateRange.dateFrom && dateRange.dateTo && Array.isArray(posResponse.data)) {
            const posTotal = posResponse.data.reduce((total, item) => total + Math.abs(item.pos_fee || 0), 0);
            nextFees.posFees = toNumber(posTotal);
          } else {
            nextFees.posFees = toNumber(posResponse.data[0]?.pos_fee || 0);
          }
        }
      } catch (error) {
        // keep default 0
      }
      let walkinSP = 'padala_report';
      params = {};
      if (dateRange.dateFrom && dateRange.dateTo) {
        params = {
          dateFrom: dateRange.dateFrom,
          dateTo: dateRange.dateTo
        };
        walkinSP = 'padala_report_range';
      } else {
        params = {};
      }
      try {
        const walkinResponse = await adminServices.storeProcedure2(walkinSP, params);
        const merchantWalkinResponse = await adminServices.storeProcedure2('merchant_' + walkinSP, params);
        if (walkinResponse.status === 200 && walkinResponse.data && walkinResponse.data.length > 0) {
          const walkinOnly = walkinResponse.data.filter(item => {
            const src = (item.cash_in_from || '').toString().toLowerCase();
            return src.includes('walk');
          });
          const walkinTotal = walkinOnly.reduce((total, item) => total + Math.abs(item.interest_rate || 0), 0);
          nextFees.walkinFees = toNumber(walkinTotal);
        }
        if (merchantWalkinResponse.status === 200 && merchantWalkinResponse.data && merchantWalkinResponse.data.length > 0) {
          const merchantTotal = merchantWalkinResponse.data.reduce((total, item) => total + Math.abs(item.interest_rate || 0), 0);
          nextFees.walkinFees += toNumber(merchantTotal);
        }
      } catch (error) {
        // keep default 0
      }

      // Online fees from padala report (Padala App + Ecpay)
      try {
        let onlineSP = 'padala_report';
        params = {};
        if (dateRange.dateFrom && dateRange.dateTo) {
          params = {
            dateFrom: dateRange.dateFrom,
            dateTo: dateRange.dateTo
          };
          onlineSP = 'padala_report_range';
        } else {
          params = {};
        }
        const onlineResponse = await adminServices.storeProcedure2(onlineSP, params);
        if (onlineResponse.status === 200 && Array.isArray(onlineResponse.data)) {
          const onlineRows = onlineResponse.data.filter(item => {
            const src = (item.cash_in_from || '').toString().toLowerCase();
            return src.includes('padala app') || src.includes('ecpay') || src.includes('app');
          });
          const onlineTotal = onlineRows.reduce((total, item) => total + Math.abs(item.interest_rate || 0), 0);
          nextFees.onlineFees = toNumber(onlineTotal);
        }
      } catch (error) {
        // keep default 0
      }

      // These are currently set to 0 by default; keep them explicit for clarity.
      nextFees.grossProfit = 0;
      nextFees.regularOrder = 0;
      nextFees.specialOrder = 0;
      setFees(nextFees);
      setIsIncomeReady(true);
      const computedTotalIncome = Object.values(nextFees).reduce((sum, value) => sum + toNumber(value), 0);
      //console.log('Fees fetched:', nextFees, computedTotalIncome);
    } catch (error) {
      if (error.status === 401) {
        navigate('/');
      }
      if (error.status === 429) {
        toast.error('Too many requests. Data will retry in 1 minute.');
        setTimeout(async () => {
          await getTransactionFees();
        }, 60000);
      }
      setFees(INITIAL_FEES);
      setIsIncomeReady(true);
    }
  }, [navigate, dateRange]);
  useEffect(() => {
    getTransactionFees();
  }, [getTransactionFees]);
  const hasAllValues = Object.keys(INITIAL_FEES).every(key => fees[key] !== null && fees[key] !== undefined);
  const totalIncome = React.useMemo(() => {
    return Object.values(fees).reduce((sum, value) => sum + toNumber(value), 0);
  }, [fees]);
  return <div className="card" style={{
    width: '100%',
    marginBottom: '20px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  }}>
      <div className="card-header" style={{
      backgroundColor: '#f8f9fa',
      borderBottom: '1px solid #e0e0e0',
      padding: '15px',
      textAlign: 'center'
    }}>
        <h5 style={{
        margin: 0,
        color: '#333',
        fontWeight: 'bold'
      }}>Gross Profit</h5>
        <p style={{
        margin: '5px 0 0 0',
        color: '#666',
        fontSize: '14px'
      }}>
           TOTAL INCOME OF IPX
        </p>
      </div>
      <div className="card-body" style={{
      padding: '20px',
      textAlign: 'center'
    }}>
        <h2 style={{
        fontSize: '2.5rem',
        fontWeight: 'bold',
        color: '#333',
        margin: '0 0 10px 0'
      }}>
          {isIncomeReady && hasAllValues ? formatCurrency(totalIncome) : '—'}
        </h2>
        <p style={{
        color: '#666',
        fontSize: '14px',
        margin: 0
      }}>
          Process Fee + Loading Fee + Profit
        </p>
      </div>
    </div>;
};
export default TotalIncomeCard;